import { type SessionCreatedEntryPoint } from '@/models/analytics/features/sessions';
import { isNetworkCreateError } from '@/utils/http/networkCreateError';
import { logger } from '@/utils/logger/logger';
import {
  appendCheckpoint,
  finishTracking,
  recordSessionFlowIntent,
  type SessionFlowFailure,
  startTracking,
  type TrackingHandle,
  TrackingKey,
} from '@heidi/runtime/performance';

// `session_create` client metrics: an accepted create opens the attempt and
// emits intent, the session page's first render closes it as `success`, and
// every failure closes it here with a bounded `error_type`.

type SessionCreateTarget =
  | { key: TrackingKey; handle?: never }
  | { handle: TrackingHandle };

const DEFAULT_TARGET: SessionCreateTarget = { key: TrackingKey.SCRIBE_NEW_SESSION };

/** Diagnostic only: rides in RUM metadata, not a metric tag. */
export type SessionCreateMode = 'online' | 'offline';

/** Create surfaces with no `SessionCreatedEntryPoint` analytics member. */
export type SessionCreateExtraEntryPoint =
  | 'shortcut'
  | 'mobile_sessions_panel'
  | 'dictate_cross_sell'
  | 'heidi_remote';

/** Bounded attempt metadata: closed vocabularies only, never ids or free text. */
export type SessionCreateMetadata = {
  entryPoint?: SessionCreatedEntryPoint | SessionCreateExtraEntryPoint;
  experiment?: string;
  create_mode?: SessionCreateMode;
};

export type SessionCreateSource = 'rust_auto_session' | 'frontend';

export type SessionCreateErrorName =
  | 'SessionCreateNetworkUnavailable'
  | 'SessionCreateTimeout'
  | 'SessionCreateServerError'
  | 'SessionCreateRejected'
  | 'SessionCreateForbidden'
  | 'SessionCreateInterrupted'
  | 'SessionCreateOfflineStoreFailed'
  | 'SessionCreateCancelled'
  | 'SessionCreateUnclassified';

/**
 * The `session_create` failure vocabulary, registered with the rest of the
 * session-flow names. `SessionCreateUnclassified` is absent on purpose, so it
 * lands in `_OTHER`.
 */
export const SESSION_CREATE_FAILURE_TYPES: Readonly<
  Partial<Record<SessionCreateErrorName, SessionFlowFailure>>
> = {
  SessionCreateNetworkUnavailable: {
    outcome: 'failed',
    errorType: 'network_unavailable',
  },
  SessionCreateTimeout: { outcome: 'failed', errorType: 'request_timeout' },
  SessionCreateServerError: { outcome: 'failed', errorType: 'server_error' },
  SessionCreateRejected: { outcome: 'failed', errorType: 'request_rejected' },
  SessionCreateForbidden: { outcome: 'blocked', errorType: 'permission_denied' },
  // Session expiry or logout ended the create; the user did not see it fail.
  SessionCreateInterrupted: { outcome: 'cancelled' },
  SessionCreateOfflineStoreFailed: {
    outcome: 'failed',
    errorType: 'offline_store_failed',
  },
  SessionCreateCancelled: { outcome: 'cancelled' },
};

const namedError = (name: SessionCreateErrorName): Error => {
  const error = new Error(name);
  error.name = name;
  return error;
};

/**
 * Maps a create rejection to a bounded name; an unknown cause lands in `_OTHER`.
 * The raw value is never forwarded: a name like `AbortError` means something
 * else for another operation in the shared name map.
 */
export const classifySessionCreateError = (error: unknown): Error => {
  const { code, response } = (error ?? {}) as {
    code?: unknown;
    response?: { status?: unknown };
  };
  // The 60s deadline surfaces as axios `CanceledError`; a DOMException covers
  // a non-axios transport.
  if (
    code === 'ERR_CANCELED' ||
    (error instanceof DOMException && error.name === 'TimeoutError')
  ) {
    return namedError('SessionCreateTimeout');
  }

  const status = response?.status;
  if (typeof status === 'number') {
    if (status === 401) return namedError('SessionCreateInterrupted');
    if (status === 403) return namedError('SessionCreateForbidden');
    if (status === 408 || status === 504) return namedError('SessionCreateTimeout');
    if (status >= 500) return namedError('SessionCreateServerError');
    if (status >= 400) return namedError('SessionCreateRejected');
  }

  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return namedError('SessionCreateTimeout');
  }
  if (code === 'ERR_NETWORK' || isNetworkCreateError(error)) {
    return namedError('SessionCreateNetworkUnavailable');
  }
  return namedError('SessionCreateUnclassified');
};

// Telemetry must never change the create's outcome.
const safely = (step: string, run: () => void): void => {
  try {
    run();
  } catch (error) {
    logger.warn('[sessionCreateTracking] telemetry step failed', {
      step,
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }
};

/**
 * Passed to a create by the caller that opened the attempt, so only that
 * create closes it on failure.
 */
export interface SessionCreateAttemptOwner {
  /** True when the caller re-creates offline on this error; that create closes it. */
  defersToOfflineFallback?: (error: unknown) => boolean;
}

/** Opens the attempt and emits intent. Call once the create is accepted. */
export const beginSessionCreateAttempt = (metadata?: SessionCreateMetadata): void =>
  safely('begin', () => {
    startTracking({ key: TrackingKey.SCRIBE_NEW_SESSION, metadata });
    recordSessionFlowIntent(TrackingKey.SCRIBE_NEW_SESSION);
  });

/**
 * Same as {@link beginSessionCreateAttempt}, but instanced, for a caller that
 * closes its own attempt and must not touch the shared UI entry.
 */
export const beginOwnedSessionCreateAttempt = (
  metadata?: SessionCreateMetadata
): TrackingHandle | undefined => {
  let handle: TrackingHandle | undefined;
  safely('begin_owned', () => {
    handle = startTracking({
      key: TrackingKey.SCRIBE_NEW_SESSION,
      instanced: true,
      metadata,
    });
    recordSessionFlowIntent(TrackingKey.SCRIBE_NEW_SESSION);
  });
  return handle;
};

/** Records which creator the attempt was routed to. */
export const markSessionCreateMode = (mode: SessionCreateMode): void =>
  safely('mark_mode', () => {
    appendCheckpoint({
      key: TrackingKey.SCRIBE_NEW_SESSION,
      checkpointName: `routed_${mode}`,
      metadata: { create_mode: mode },
    });
  });

export const completeSessionCreateAttempt = (
  target: SessionCreateTarget = DEFAULT_TARGET,
  metadata?: { create_source: SessionCreateSource }
): void => safely('complete', () => void finishTracking({ ...target, metadata }));

/** Closes the attempt with a classified create rejection. */
export const failSessionCreateAttempt = (
  error: unknown,
  target: SessionCreateTarget = DEFAULT_TARGET
): void =>
  safely('fail', () => {
    finishTracking({
      ...target,
      outcome: 'failed',
      error: classifySessionCreateError(error),
    });
  });

/** The online create's failure: closes the attempt unless the caller defers it. */
export const failOwnedSessionCreateAttempt = (
  owner: SessionCreateAttemptOwner | undefined,
  error: unknown
): void => {
  if (!owner || owner.defersToOfflineFallback?.(error)) return;
  failSessionCreateAttempt(error);
};

/**
 * Records a failed redirect without closing the attempt: a retry from the
 * recovery toast can still mount the page, which then records the success.
 */
export const markSessionCreateNavigationFailed = (): void =>
  safely('mark_navigation_failed', () => {
    appendCheckpoint({
      key: TrackingKey.SCRIBE_NEW_SESSION,
      checkpointName: 'navigation_failed',
      metadata: { navigation_failed: true },
    });
  });

/** Closes the attempt with a failure whose cause the caller already knows. */
export const failSessionCreateAttemptAs = (
  name: 'SessionCreateOfflineStoreFailed',
  target: SessionCreateTarget = DEFAULT_TARGET
): void =>
  safely('fail_as', () => {
    finishTracking({ ...target, outcome: 'failed', error: namedError(name) });
  });

/**
 * Closes the attempt as `cancelled`: not ready to create, or a logout ended
 * the create. `finishTracking` only accepts `completed | failed`, so this
 * raises a named failure that the registered name map turns into `cancelled`.
 */
export const cancelSessionCreateAttempt = (
  reason: 'interrupted' | 'not_ready',
  target: SessionCreateTarget = DEFAULT_TARGET
): void =>
  safely('cancel', () => {
    finishTracking({
      ...target,
      outcome: 'failed',
      error: namedError(
        reason === 'interrupted' ? 'SessionCreateInterrupted' : 'SessionCreateCancelled'
      ),
      metadata: { cancel_reason: reason },
    });
  });
